import type { FeatureFlagKey } from "@rebuild/shared";

import { Role } from "@/enums/role";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";

/**
 * True when the current user is a customer org admin (`Role.ADMIN`, not the
 * legacy admin/creator combo) AND their org has the given feature flag enabled.
 *
 * Returns `false` while feature flags are still hydrating, which is the right
 * default for showing/hiding admin UI. Route guards that need to wait on the
 * flag before redirecting (see `OrgAdminRouteWrapper`) should read `loading`
 * from `useFeatureFlags` directly.
 *
 * @example
 *   const canManageTeam = useHasAdminFeatureAccess("SELF_SERVE_ADMIN_ENABLED");
 */
export function useHasAdminFeatureAccess(flag: FeatureFlagKey) {
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  return user?.role === Role.ADMIN && isFeatureEnabled(flag);
}
